import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import React, { useContext, useMemo, useState } from 'react';
import { BinContext } from '../../../../../context/BinContext';
import { ContainerContext } from '../../../../../context/ContainerContext';
import { ContainerTypeContext } from '../../../../../context/ContainerTypeContext';
import { LotContext } from '../../../../../context/LotContext';
import { ProductContext } from '../../../../../context/ProductContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { Container } from '../../../../../types/container';
import { Product } from '../../../../../types/product';
import { t, translate } from '../../../../../types/translation/Translator';
import { toFilterString } from '../../../../../util/string.util';
import {
  ProductOrContainerContainerColumn,
  ProductOrContainerNameColumn,
} from '../../../../components/Table/ProductOrContainer';
import Table, { TableHeader } from '../../../../components/Table';
import { Searchbar } from '../../../../components/TextInput';
import { ContainerUtil } from '../../../../../util/containers.util';
import { AddButton, ButtonVariant, DeleteButton } from '../../../../components/Button';
import { IconButton } from '../../../../icons';
import { IconType } from '../../../../icons/Icon';
import ContainerQuickActionModal from '../QuickAction/ContainerQuickActionModal';
import GenerateBarcodeButton from '../../../../components/Button/templates/GenerateBarcodeButton';
import { BarcodeExportEntityType } from '../../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { UnitOfMeasure } from '../../../../../types/uom';
import QuickActionModal from '../../Product/Detail/Info/QuickActionModal';
import ContainerDeleteModal from './ContainerDeleteModal';

interface ContainerInfoPaneInputProps {
  container: Container;
}

export default function ContainerInfoPane({ container }: ContainerInfoPaneInputProps) {
  const { products } = useContext(ProductContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { lots } = useContext(LotContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { bins } = useContext(BinContext);
  const { containerTypes, containerTypesLoading } = useContext(ContainerTypeContext);
  const { containers, containersLoading } = useContext(ContainerContext);

  const { childProducts, childContainers } = useMemo(() => {
    return ContainerUtil.getAllContainerChildren(containers, products, container);
  }, [container, containers, products]);

  const allItems = useMemo(() => {
    return [...childProducts, ...childContainers];
  }, [childProducts, childContainers]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [quickActionModalOpen, setQuickActionModalOpen] = useState(false);
  const [addProductTransactionsModalOpen, setAddProductTransactionsModalOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const [selectedContainer, setSelectedContainer] = useState<Container | undefined>();

  const handleFilter = (item: Product | Container) => {
    if (item instanceof Product) {
      const productName = productMasterData.get(item.productMasterDataId)?.productName;
      const productNumber = productMasterData.get(item.productMasterDataId)?.productNumber;

      if (toFilterString(productName).includes(filter) || toFilterString(productNumber).includes(filter)) {
        return true;
      }
    }

    if (item instanceof Container) {
      if (toFilterString(item.identifier).includes(filter)) return true;
    }

    return false;
  };

  const hasLPN = !!childProducts.filter(item => item.lpn).length;
  const hasSerial = !!childProducts.filter(item => item.serial).length;
  const hasLot = !!childProducts.filter(item => item.lotId).length;

  const headers: TableHeader<Product | Container>[] = [
    {
      key: 'name',
      name: t(TranslationKey.name).singular.label,
      text: (item: Product | Container) =>
        ProductOrContainerNameColumn({
          productMasterDataId: item instanceof Product && item.productMasterDataId,
          containerId: item instanceof Container && item.id,
          productMasterData,
          containers,
        }),
    },
    {
      key: 'number',
      name: t(TranslationKey.number).singular.label,
      text: (item: Product | Container) =>
        item instanceof Product
          ? productMasterData.get(item.productMasterDataId)?.productNumber || 'Unknown Product Number'
          : '',
    },
    {
      key: 'lpn',
      name: t(TranslationKey.lpn).singular.label,
      text: (item: Product | Container) => (item instanceof Product ? item.lpn || '' : ''),
    },
    {
      key: 'serialNumber',
      name: t(TranslationKey.serial).singular.label,
      text: (item: Product | Container) => (item instanceof Product ? item.serial || '' : ''),
    },
    {
      key: 'lotNumber',
      name: t(TranslationKey.lotNumber).singular.label,
      text: (item: Product | Container) => (item instanceof Product ? lots.get(item.lotId || '')?.number || '' : ''),
    },
    {
      key: 'container',
      name: t(TranslationKey.container).singular.label,
      text: (item: Product | Container) => ProductOrContainerContainerColumn(item, containers),
    },
    {
      key: 'quantity',
      sortValue: (item: Product | Container) => (item instanceof Product ? item.unitQuantity : 1),
      name: t(TranslationKey.quantity).singular.label,
      text: (item: Product | Container) =>
        item instanceof Product
          ? `${UnitOfMeasure.toQuantityWithSuffix(
              productMasterData.get(item.productMasterDataId)?.unitOfMeasure,
              item.unitQuantity,
            )}`
          : '',
    },
  ].filter(item => {
    if (item.key === 'serialNumber' && !hasSerial) return false;
    if (item.key === 'lpn' && !hasLPN) return false;
    if (item.key === 'lotNumber' && !hasLot) return false;

    return true;
  });

  const binName = bins.get(container.binId || '')?.name;
  const stockLocationName = stockLocations.get(container.stockLocationId || '')?.name;

  const handleClick = (item: Product | Container) => {
    if (item instanceof Product) {
      setSelectedProduct(item);
    } else if (item instanceof Container) {
      setSelectedContainer(item);
    }
  };

  return (
    <>
      {(selectedProduct || addProductTransactionsModalOpen) && (
        <QuickActionModal
          open={!!selectedProduct || addProductTransactionsModalOpen}
          setOpen={open => {
            if (!open) {
              setSelectedProduct(undefined);
              setAddProductTransactionsModalOpen(false);
            }
          }}
          productId={selectedProduct?.id}
          pmdId={selectedProduct?.productMasterDataId}
          props={{ toContainerId: selectedProduct ? undefined : container.id }}
        />
      )}

      {(selectedContainer || quickActionModalOpen) && (
        <ContainerQuickActionModal
          open={!!selectedContainer || quickActionModalOpen}
          setOpen={open => {
            if (!open) {
              setSelectedContainer(undefined);
              setQuickActionModalOpen(false);
            }
          }}
          container={selectedContainer || container}
        />
      )}

      {deleteModalOpen && (
        <ContainerDeleteModal open={deleteModalOpen} setOpen={setDeleteModalOpen} container={container} />
      )}

      <div className='content-x flex flex-grow flex-col gap-8'>
        <div className='grid grid-cols-4 gap-4'>
          <div className='grid gap-2'>
            <p className='font-semibold'>{t(TranslationKey.container).singular.label}</p>
            <p className='text-xl font-bold text-gray-400'>{container.identifier}</p>
          </div>

          <div className='grid gap-2'>
            {stockLocationName ? (
              <>
                <p className='font-semibold'>{t(TranslationKey.stockLocation).singular.label}</p>
                <p className='text-xl font-bold text-gray-400'>{stockLocationName}</p>
              </>
            ) : null}
          </div>
          <div className='grid gap-2'>
            {binName ? (
              <>
                <p className='font-semibold'>{t(TranslationKey.bin).singular.label}</p>
                <p className='text-xl font-bold text-gray-400'>{bins.get(container.binId || '')?.name || ''}</p>
              </>
            ) : null}
          </div>

          <div className='grid gap-2'>
            <p className='font-semibold'>{t(TranslationKey.containerType).singular.label}</p>
            <p className='text-xl font-bold text-gray-400'>
              {containerTypes.get(container.containerTypeId || '')?.name || ''}
            </p>
          </div>
        </div>

        <div className='flex flex-grow flex-col gap-2'>
          <div className='flex justify-between gap-4'>
            <Searchbar
              value={filter}
              onChange={value => setFilter(value)}
              placeholder={t(TranslationKey.filterProducts).singular.label}
            />
            <div className='flex gap-2'>
              <GenerateBarcodeButton
                text={translate({ key: TranslationKey.generateBarcode, count: 'plural' })}
                entityType={BarcodeExportEntityType.product}
                entityIds={allItems.filter(handleFilter).map(i => i.id)}
                variant={ButtonVariant.secondary}
              />
              <IconButton
                type={IconType.switchVertical1}
                onClick={() => setQuickActionModalOpen(true)}
                text={t(TranslationKey.quickAction).singular.upper}
              />
              <AddButton
                text={t(TranslationKey.addProduct).singular.label}
                onClick={() => setAddProductTransactionsModalOpen(true)}
              />
            </div>
          </div>
          <Table
            loading={containersLoading || containerTypesLoading}
            items={allItems.filter(handleFilter)}
            headers={headers}
            onClick={handleClick}
          />
          <div className='flex justify-between'>
            <DeleteButton onClick={() => setDeleteModalOpen(true)} />
            <GenerateBarcodeButton
              text={'Generate container barcode'}
              entityType={BarcodeExportEntityType.container}
              entityIds={[container.id]}
            />
          </div>
        </div>
      </div>
    </>
  );
}
